import React, { useEffect, useState } from 'react';
import useGameStore from './engine/store';
import MenuScreen from './components/MenuScreen';
import OpponentPanel from './components/OpponentPanel';
import ChatPanel from './components/ChatPanel';
import ActionPanel from './components/ActionPanel';
import StatusBar from './components/StatusBar';
import ResultsScreen from './components/ResultsScreen';
import CareerScreen from './components/CareerScreen';

function GameScreen() {
  const { tick, timerActive, screen } = useGameStore();
  const [mobileTab, setMobileTab] = useState(null);

  // Timer
  useEffect(() => {
    if (!timerActive || screen !== 'playing') return;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [timerActive, tick, screen]);

  return (
    <div className="h-screen flex flex-col bg-dark-900 bg-grid overflow-hidden">
      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Status + Opponent (desktop) */}
        <div className="w-72 border-r border-white/5 bg-dark-800/40 flex-shrink-0 hidden lg:flex flex-col overflow-hidden">
          <div className="overflow-y-auto flex-1">
            <div className="p-3">
              <OpponentPanel />
            </div>
            <StatusBar />
          </div>
        </div>

        {/* Center: Chat */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile top bar */}
          <div className="lg:hidden h-12 bg-dark-800/80 backdrop-blur-md border-b border-white/5 flex items-center px-3 gap-3">
            <MobileTopInfo />
          </div>

          <ChatPanel />
          <ActionPanel />
        </div>
      </div>

      {/* Mobile slide panels */}
      <MobilePanel active={mobileTab} setActive={setMobileTab} />
    </div>
  );
}

function MobileTopInfo() {
  const { scenario, timeRemaining, tensionLevel, opponentName, personalityData, round } = useGameStore();

  const mins = Math.floor(timeRemaining / 60);
  const secs = timeRemaining % 60;
  const timeColor = timeRemaining > 30 ? '#00ff88' : timeRemaining > 15 ? '#ffcc00' : '#ff3366';

  return (
    <>
      <div className="flex items-center gap-2 flex-1">
        <span className="text-lg">{personalityData?.avatar}</span>
        <span className="text-sm text-white font-semibold truncate">{opponentName}</span>
        <span className="text-xs px-1.5 py-0.5 rounded" style={{
          background: `${personalityData?.color}20`,
          color: personalityData?.color,
        }}>
          {personalityData?.emoji}
        </span>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <span className="text-gray-500">R{round}</span>
        <span className="font-mono font-bold" style={{ color: timeColor }}>
          {mins}:{secs.toString().padStart(2, '0')}
        </span>
      </div>
    </>
  );
}

function MobilePanel({ active, setActive }) {
  return (
    <>
      {active && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setActive(null)} />
          <div className="absolute inset-y-0 left-0 w-80 bg-dark-800 shadow-2xl overflow-y-auto">
            <div className="p-3">
              <OpponentPanel />
            </div>
            <StatusBar />
          </div>
        </div>
      )}

      <div className="lg:hidden h-12 bg-dark-800/90 backdrop-blur-md border-t border-white/5 flex items-center justify-around px-4">
        <button
          onClick={() => setActive(active ? null : 'info')}
          className={`flex flex-col items-center gap-0.5 ${active ? 'text-neon-blue' : 'text-gray-500'}`}
        >
          <span className="text-base">🧠</span>
          <span className="text-[10px]">Opponent</span>
        </button>
        <button
          onClick={() => setActive(null)}
          className="flex flex-col items-center gap-0.5 text-gray-500"
        >
          <span className="text-base">💬</span>
          <span className="text-[10px]">Chat</span>
        </button>
      </div>
    </>
  );
}

export default function App() {
  const { screen } = useGameStore();

  switch (screen) {
    case 'menu': return <MenuScreen />;
    case 'playing': return <GameScreen />;
    case 'results': return <ResultsScreen />;
    case 'career': return <CareerScreen />;
    default: return <MenuScreen />;
  }
}
